import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTimezone } from '../contexts/TimezoneContext';
import api from '../api';
import { invalidate } from './useResource';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import { linkToCommitment } from '../utils/commitmentLink';
import { useUndo } from '../contexts/UndoContext';
import { safeStorage } from '../utils/safeStorage';
import { celebrateRow, celebrateRows } from '../utils/celebrateRow';
import { formatTime, todayIST } from '../utils/locale';

const TASK_TYPE_TO_BLOCK = { CALL: 'CALL', PAYMENT: 'PAYMENT' };
const getBlockType = (taskType) => TASK_TYPE_TO_BLOCK[taskType] || 'TASK';

export default function useTodayActions({
  tasks, setTasks, blocks, setBlocks,
  today, fetchAll, fetchDecisions, fetchTasks,
}) {
  const { activeBusiness, businesses } = useBusiness();
  const { preferences } = usePreferences();
  const { timezone: tzid } = useTimezone();
  const { capture: captureUndo } = useUndo();
  const navigate = useNavigate();
  const tomorrow = format(addDays(new Date(today), 1), 'yyyy-MM-dd');

  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [quickAdd, setQuickAdd] = useState('');
  // Default quick-add destination respects user preference (today | inbox).
  // 'tomorrow' is still selectable, just not the default.
  const [quickAddSchedule, setQuickAddSchedule] = useState(
    preferences.defaultNewTaskDestination === 'inbox' ? 'inbox' : 'today',
  );
  const [planningDay, setPlanningDay] = useState(false);
  const [taskLinkPicker, setTaskLinkPicker] = useState(null);
  const [sweepMode, setSweepMode] = useState(false);
  const [followUpItem, setFollowUpItem] = useState(null);
  const [delegateItem, setDelegateItem] = useState(null);

  // ── Toggle complete (optimistic) ──
  const handleToggleComplete = async (item) => {
    const task = tasks.find(t => t.id === item.id);
    if (!task) return;
    const newStatus = task.status === 'COMPLETED' ? 'TODAY' : 'COMPLETED';
    const snapshot = [...tasks];
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus, completedAt: newStatus === 'COMPLETED' ? new Date().toISOString() : null } : t));
    // Warm-amber sparkle on the row when checking OFF — micro-celebration
    // for completion. Skipped when un-checking (toggling back to TODAY).
    if (newStatus === 'COMPLETED') {
      celebrateRow(`decision-item-${task.id}`);
    }
    // Throttle follow-up modal: only fire for high-signal completions.
    // Avoids modal fatigue on routine low-priority checkoffs.
    const shouldAskFollowUp = newStatus === 'COMPLETED' && (
      task.priority === 'URGENT' || task.priority === 'HIGH' ||
      task.taskType === 'CALL' || task.taskType === 'PAYMENT'
    );
    if (shouldAskFollowUp) setFollowUpItem({ ...task, sourceType: 'task' });
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      fetchDecisions();
      // Cross-component: refresh audit timeline + stakeholder pressure + pendency radar
      invalidate(['activity-timeline', 'stakeholder-heat', 'pendency-radar-budgets', 'reality-snapshot-velocity']);
    } catch {
      setTasks(snapshot);
      if (shouldAskFollowUp) setFollowUpItem(null);
      toast.error('Toggle failed');
    }
  };

  // ── Defer ──
  const handleDeferTask = async (item) => {
    if (item.type !== 'task') return;
    try {
      const res = await api.post(`/tasks/${item.id}/defer`);
      const newCount = res.data?.deferralCount || 0;
      const prevCount = Math.max(0, newCount - 1);
      if (newCount >= 5) toast.message('Moved to Graveyard', { description: `"${item.title}" deferred ${newCount} times. Archive or revive it.` });
      else if (newCount >= 3) toast.warning(`Deferred ${newCount} times`, { description: 'Consider delegating, timeboxing 15m, or archiving.' });
      else {
        captureUndo({
          label: `Deferred "${item.title}"`,
          undo: async () => {
            await api.put(`/tasks/${item.id}`, { deferralCount: prevCount });
            await Promise.all([fetchTasks(), fetchDecisions()]);
          },
        });
      }
      await Promise.all([fetchTasks(), fetchDecisions()]);
    } catch { toast.error('Could not defer'); }
  };

  // ── Cross-entity actions (optimistic via decisions list) ──
  const handleCallAction = async (item, action) => {
    if (action === 'complete') {
      setFollowUpItem({ ...item, sourceType: 'call' });
      celebrateRow(`decision-item-${item.id}`);
    }
    try {
      if (action === 'complete') await api.put(`/calls/${item.id}`, { status: 'COMPLETED', outcome: 'COMPLETED' });
      fetchDecisions();
    } catch {
      if (action === 'complete') setFollowUpItem(null);
      toast.error('Could not update call');
    }
  };

  const handlePaymentAction = async (item, action) => {
    if (action === 'paid') {
      setFollowUpItem({ ...item, sourceType: 'payment' });
      celebrateRow(`decision-item-${item.id}`);
    }
    try {
      if (action === 'paid') await api.put(`/payments/${item.id}`, { status: 'PAID', paymentDate: todayIST(tzid) });
      fetchDecisions();
    } catch {
      if (action === 'paid') setFollowUpItem(null);
      toast.error('Could not update payment');
    }
  };

  const handleReadingAction = async (item, action) => {
    if (action === 'read') {
      setFollowUpItem({ ...item, sourceType: 'reading' });
      celebrateRow(`decision-item-${item.id}`);
    }
    try {
      if (action === 'read') await api.put(`/reading/${item.id}`, { status: 'READ' });
      fetchDecisions();
    } catch {
      if (action === 'read') setFollowUpItem(null);
      toast.error('Could not update reading item');
    }
  };

  // ── Archive (optimistic + ⌘Z undo) ──
  const handleArchiveDecision = async (item) => {
    const snapshot = [...tasks];
    const prevStatus = item.status || 'TODAY';
    if (item.type === 'task') {
      setTasks(prev => prev.map(t => t.id === item.id ? { ...t, status: 'ARCHIVED' } : t));
    }
    try {
      if (item.type === 'task') {
        await api.put(`/tasks/${item.id}`, { status: 'ARCHIVED' });
        captureUndo({
          label: `Archived "${item.title}"`,
          undo: async () => {
            await api.put(`/tasks/${item.id}`, { status: prevStatus });
            await Promise.all([fetchTasks(), fetchDecisions()]);
          },
        });
      }
      await Promise.all([fetchTasks(), fetchDecisions()]);
    } catch {
      setTasks(snapshot);
      toast.error('Could not archive');
    }
  };

  // ── Sweep actions ──
  const handleSweepAction = async (item, action) => {
    switch (action) {
      case 'do': await handleSlotTask(item); break;
      case 'delegate': if (item.type === 'task') setDelegateItem(item); break;
      case 'defer': if (item.type === 'task') await handleDeferTask(item); break;
      case 'archive':
        if (item.type === 'task') await handleArchiveDecision(item);
        else if (item.type === 'call') await handleCallAction(item, 'complete');
        else if (item.type === 'payment') await handlePaymentAction(item, 'paid');
        else if (item.type === 'reading') await handleReadingAction(item, 'read');
        break;
      default: break;
    }
  };

  // ── Slot task into timeline ──
  const handleSlotTask = async (item) => {
    const duration = item.estimatedMinutes || 30;
    const occupied = blocks.filter(b => b.date === today).map(b => {
      const [sH, sM] = (b.startTime || '0:0').split(':').map(Number);
      const [eH, eM] = (b.endTime || '0:0').split(':').map(Number);
      return [sH * 60 + sM, eH * 60 + eM];
    }).sort((a, b) => a[0] - b[0]);

    let cursor = Math.max(8 * 60, Math.ceil((new Date().getHours() * 60 + new Date().getMinutes()) / 15) * 15);
    let slotStart = null;
    while (cursor + duration <= 20 * 60) {
      const end = cursor + duration;
      if (!occupied.some(([s, e]) => !(end <= s || cursor >= e))) { slotStart = cursor; break; }
      const conflict = occupied.find(([s, e]) => !(end <= s || cursor >= e));
      cursor = (conflict ? conflict[1] : cursor) + 15;
    }

    if (slotStart === null) { toast.message('No open slot'); return; }

    const startTime = `${Math.floor(slotStart / 60).toString().padStart(2, '0')}:${(slotStart % 60).toString().padStart(2, '0')}`;
    const endMin = slotStart + duration;
    const endTime = `${Math.floor(endMin / 60).toString().padStart(2, '0')}:${(endMin % 60).toString().padStart(2, '0')}`;

    try {
      const res = await api.post('/timeblocks', {
        date: today, startTime, endTime,
        blockType: getBlockType(item.taskType),
        title: item.title, taskId: item.id,
        businessId: item.businessId || activeBusiness?.id || businesses[0]?.id || null,
        tzid,
      });
      setBlocks(prev => [...prev, res.data]);
      toast.success(`Slotted at ${startTime}`);
    } catch { toast.error('Could not slot'); }
  };

  // ── Select / navigate ──
  const handleSelectDecision = async (item) => {
    if (item.type === 'task') {
      const task = tasks.find(t => t.id === item.id);
      if (task) { setEditTask(task); return; }
      // Task not in the locally loaded list (e.g., LATER/deferred) — fetch it.
      try {
        const res = await api.get(`/tasks/${item.id}`);
        setEditTask(res.data);
      } catch { toast.error('Could not open task'); }
    } else if (item.type === 'call') navigate('/calls');
    else if (item.type === 'payment') navigate('/payments');
    else if (item.type === 'reading') navigate('/reading');
  };

  // ── Delegate ──
  const handleDelegateTask = async (item, delegateTo, notes, staffId, expectedReturnDate, isAdHoc) => {
    try {
      await api.post(`/tasks/${item.id}/delegate`, {
        delegatedTo: delegateTo || 'Team',
        delegationNotes: notes || '',
        staffId: staffId || null,
        expectedReturnDate: expectedReturnDate || null,
        isAdHoc: isAdHoc || false,
      });
      await Promise.all([fetchTasks(), fetchDecisions()]);
      toast.success(`Delegated "${item.title}" to ${delegateTo || 'team'}`);
      setDelegateItem(null);
    } catch { toast.error('Could not delegate'); }
  };

  // ── Block click / shift ──
  const handleBlockClick = (block) => {
    const task = tasks.find(t => t.id === block.taskId || t.title === block.title);
    if (task) setEditTask(task);
    else toast.message('Block not linked to a task');
  };

  const handleShiftBlock = async (block, shiftMinutes) => {
    const [sH, sM] = (block.startTime || '0:0').split(':').map(Number);
    const [eH, eM] = (block.endTime || '0:0').split(':').map(Number);
    const duration = (eH * 60 + eM) - (sH * 60 + sM);
    let newStart = (sH * 60 + sM) + shiftMinutes;
    if (newStart < 8 * 60) newStart = 8 * 60;
    if (newStart + duration > 20 * 60) { toast.message('Past end of day'); return; }
    const startTime = `${Math.floor(newStart / 60).toString().padStart(2, '0')}:${(newStart % 60).toString().padStart(2, '0')}`;
    const endTime = `${Math.floor((newStart + duration) / 60).toString().padStart(2, '0')}:${((newStart + duration) % 60).toString().padStart(2, '0')}`;
    try {
      const res = await api.put(`/timeblocks/${block.id}`, { startTime, endTime, tzid });
      setBlocks(prev => prev.map(b => b.id === block.id ? res.data : b));
      toast.success(`Moved to ${formatTime(startTime)}`);
    } catch { toast.error('Could not move block'); }
  };

  // ── Schedule payload builder (DRY helper) ──
  const buildSchedulePayload = (formData, base) => {
    const { schedulePreference, scheduledDateTime, ...taskPayload } = formData;
    let payload = { ...base, ...taskPayload };
    if (schedulePreference === 'tomorrow') {
      payload = { ...payload, scheduledDate: tomorrow, status: 'SCHEDULED' };
    } else if (schedulePreference === 'pick-datetime' && scheduledDateTime) {
      const pickedDate = scheduledDateTime.split('T')[0];
      payload = { ...payload, scheduledDate: pickedDate, status: pickedDate === today ? 'TODAY' : 'SCHEDULED' };
    }
    return { payload, schedulePreference, scheduledDateTime };
  };

  const maybeCreateTimeblock = async (schedulePreference, scheduledDateTime, task) => {
    if ((schedulePreference === 'today-time' || schedulePreference === 'pick-datetime') && scheduledDateTime) {
      const [datePart, timePart] = scheduledDateTime.split('T');
      const slotDate = schedulePreference === 'today-time' ? today : datePart;
      const duration = task.estimatedMinutes || 30;
      const [hour, minute] = timePart.split(':').map(Number);
      const endMinutes = hour * 60 + minute + duration;
      await api.post('/timeblocks', {
        date: slotDate,
        startTime: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
        endTime: `${Math.floor(endMinutes / 60).toString().padStart(2, '0')}:${(endMinutes % 60).toString().padStart(2, '0')}`,
        blockType: getBlockType(task.taskType),
        title: task.title, taskId: task.id,
        businessId: task.businessId || activeBusiness?.id || businesses[0]?.id || null,
        tzid,
      });
    }
  };

  // ── Create task ──
  const handleCreateTask = async (formData) => {
    try {
      const { payload, schedulePreference, scheduledDateTime } = buildSchedulePayload(formData, { scheduledDate: today, status: 'TODAY' });
      const res = await api.post('/tasks', payload);
      await maybeCreateTimeblock(schedulePreference, scheduledDateTime, res.data);
      if (formData.commitmentId) await linkToCommitment(formData.commitmentId, res.data.id, 'task');
      setShowModal(false);
      fetchAll();
      toast.success('Task created');
    } catch { toast.error('Create failed'); }
  };

  // ── Update task ──
  const handleUpdateTask = async (formData) => {
    try {
      const { payload, schedulePreference, scheduledDateTime } = buildSchedulePayload(formData, {});
      await api.put(`/tasks/${editTask.id}`, { ...payload, version: editTask?.version });
      await maybeCreateTimeblock(schedulePreference, scheduledDateTime, { ...editTask, ...payload });
      setEditTask(null);
      fetchAll();
    } catch { toast.error('Update failed'); }
  };

  // ── Follow-up task ──
  const handleCreateFollowUp = async (formData) => {
    try {
      const { payload } = buildSchedulePayload(formData, { scheduledDate: today, status: 'TODAY' });
      await api.post('/tasks', payload);
      fetchAll();
      toast.success('Follow-up task created');
    } catch { toast.error('Could not create follow-up'); }
  };

  // ── Quick add ──
  const handleQuickAdd = async (e) => {
    if (e.key === 'Enter' && quickAdd.trim()) {
      try {
        if (quickAddSchedule === 'inbox') {
          await api.post('/inbox', {
            title: quickAdd,
            sourceType: 'MANUAL',
            businessId: activeBusiness?.id || businesses[0]?.id || null,
          });
          setQuickAdd('');
          fetchAll();
          toast.success('Captured to Inbox');
          return;
        }
        let payload = {
          title: quickAdd, status: 'TODAY', priority: 'MEDIUM',
          estimatedMinutes: 30, scheduledDate: today,
          businessId: activeBusiness?.id || businesses[0]?.id || null, taskType: 'STANDARD',
        };
        if (quickAddSchedule === 'tomorrow') {
          payload = { ...payload, scheduledDate: tomorrow, status: 'SCHEDULED' };
        }
        await api.post('/tasks', payload);
        setQuickAdd('');
        // Restore the user's preferred default after each add.
        setQuickAddSchedule(preferences.defaultNewTaskDestination === 'inbox' ? 'inbox' : 'today');
        fetchAll();
        toast.success('Task added');
      } catch { toast.error('Quick add failed'); }
    }
  };

  // ── AI Plan ──
  const [planPreview, setPlanPreview] = useState(null); // { blocks, usedFallback } or null
  const handlePlanMyDay = useCallback(async () => {
    setPlanningDay(true);
    try {
      const res = await api.post('/ai/plan-day', {
        date: today, businessId: activeBusiness?.id || null,
        focusBlockMinutes: preferences.focusBlockMinutes, preferFastResponse: true,
      });
      const proposed = res.data.blocks || [];
      if (proposed.length === 0) {
        toast.message('Your calendar is already balanced — nothing to add.');
      } else {
        // Preview-diff: surface to the user before applying.
        setPlanPreview({ blocks: proposed, usedFallback: !!res.data.usedFallback });
      }
    } catch {
      toast.error('Could not plan your day');
    } finally {
      setTimeout(() => setPlanningDay(false), 800);
    }
  }, [today, activeBusiness?.id, preferences.focusBlockMinutes]);

  const acceptPlanPreview = useCallback(() => {
    if (!planPreview) return;
    setBlocks(prev => {
      const preserved = prev.filter(b => b.generatedBy !== 'AI_PLAN');
      return [...preserved, ...planPreview.blocks].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });
    fetchDecisions();
    toast.success(`Plan accepted — ${planPreview.blocks.length} blocks added`, {
      description: planPreview.usedFallback ? 'Smart backup plan applied.' : 'AI schedule applied.',
    });
    setPlanPreview(null);
  }, [planPreview, setBlocks, fetchDecisions]);

  const discardPlanPreview = useCallback(() => {
    setPlanPreview(null);
    toast.message('Plan discarded');
  }, []);

  // Auto-plan on page load trigger
  useEffect(() => {
    const shouldRunPlan = safeStorage.getString('polkdesq_run_plan_day');
    if (shouldRunPlan === 'true') {
      safeStorage.remove('polkdesq_run_plan_day');
      handlePlanMyDay();
    }
  }, [handlePlanMyDay]);

  // ── Bulk actions ──
  const handleBulkComplete = async (selectedDecisions) => {
    const taskItems = selectedDecisions.filter(d => d.type === 'task');
    if (taskItems.length === 0) { toast.message('No tasks selected'); return; }
    const snapshot = [...tasks];
    const taskIds = taskItems.map(d => d.id);
    setTasks(prev => prev.map(t => taskIds.includes(t.id) ? { ...t, status: 'COMPLETED', completedAt: new Date().toISOString() } : t));
    // Warm amber sparkle cascade — 60ms stagger across the rows
    // so a bulk complete reads as a celebratory wave.
    celebrateRows(taskIds.map(id => `decision-item-${id}`));
    try {
      await api.post('/tasks/bulk', { taskIds, action: 'complete' });
      toast.success(`${taskItems.length} task${taskItems.length > 1 ? 's' : ''} completed`);
      await Promise.all([fetchTasks(), fetchDecisions()]);
    } catch {
      setTasks(snapshot);
      toast.error('Bulk complete failed');
    }
  };

  const handleBulkDefer = async (selectedDecisions) => {
    const taskItems = selectedDecisions.filter(d => d.type === 'task');
    if (taskItems.length === 0) { toast.message('No tasks selected'); return; }
    const taskIds = taskItems.map(d => d.id);
    try {
      await api.post('/tasks/bulk', { taskIds, action: 'defer' });
      toast.success(`${taskItems.length} task${taskItems.length > 1 ? 's' : ''} deferred`);
      await Promise.all([fetchTasks(), fetchDecisions()]);
    } catch {
      toast.error('Bulk defer failed');
    }
  };

  const handleBulkArchive = async (selectedDecisions) => {
    const taskItems = selectedDecisions.filter(d => d.type === 'task');
    if (taskItems.length === 0) { toast.message('No tasks selected'); return; }
    const snapshot = [...tasks];
    const taskIds = taskItems.map(d => d.id);
    setTasks(prev => prev.map(t => taskIds.includes(t.id) ? { ...t, status: 'ARCHIVED' } : t));
    try {
      await api.post('/tasks/bulk', { taskIds, action: 'archive' });
      toast.success(`${taskItems.length} task${taskItems.length > 1 ? 's' : ''} archived`);
      await Promise.all([fetchTasks(), fetchDecisions()]);
    } catch {
      setTasks(snapshot);
      toast.error('Bulk archive failed');
    }
  };

  const handleRenameDecision = async (item, newTitle) => {
    const trimmed = (newTitle || '').trim();
    if (!trimmed) throw new Error('Title cannot be empty');
    const endpointByType = {
      task:    `/tasks/${item.id}`,
      call:    `/calls/${item.id}`,
      payment: `/payments/${item.id}`,
      reading: `/reading/${item.id}`,
    };
    const url = endpointByType[item.type];
    if (!url) throw new Error('Unsupported item type for rename');
    const fieldByType = {
      task: 'title', call: 'contactName', payment: 'title', reading: 'title',
    };
    const field = fieldByType[item.type];
    if (item.type === 'task') {
      const snapshot = [...tasks];
      setTasks((prev) => prev.map((t) => (t.id === item.id ? { ...t, title: trimmed } : t)));
      try {
        await api.put(url, { [field]: trimmed });
        toast.success('Renamed');
        fetchDecisions();
      } catch (err) {
        setTasks(snapshot);
        toast.error('Rename failed');
        throw err;
      }
    } else {
      try {
        await api.put(url, { [field]: trimmed });
        toast.success('Renamed');
        fetchDecisions();
      } catch (err) {
        toast.error('Rename failed');
        throw err;
      }
    }
  };

  return {
    showModal, setShowModal,
    editTask, setEditTask,
    quickAdd, setQuickAdd,
    quickAddSchedule, setQuickAddSchedule,
    planningDay, taskLinkPicker, setTaskLinkPicker,
    sweepMode, setSweepMode,
    followUpItem, setFollowUpItem,
    delegateItem, setDelegateItem,
    tomorrow,
    handleToggleComplete, handleDeferTask,
    handleCallAction, handlePaymentAction, handleReadingAction,
    handleArchiveDecision, handleSweepAction,
    handleSlotTask, handleSelectDecision, handleDelegateTask,
    handleBlockClick, handleShiftBlock,
    handleCreateTask, handleUpdateTask, handleCreateFollowUp,
    handleQuickAdd, handlePlanMyDay,
    planPreview, acceptPlanPreview, discardPlanPreview,
    handleBulkComplete, handleBulkDefer, handleBulkArchive,
    handleRenameDecision,
  };
}
